import { useState } from 'react';
import { Megaphone, RefreshCw, SquarePlus, X } from 'lucide-react';
import { UserLinks } from '../../../components/UserLinks';
import { useFormatUtils } from '../../../hooks/useFormatUtils';
import type { FeedbackState } from '../../../types/feedback';
import { formatTimeAgo } from '../../../utils/timeUtils';
import { CompactActionBreakdown } from './CompactActionBreakdown';

type FormatTimeSince = ReturnType<typeof useFormatUtils>['formatTimeSince'];

interface UserFeedbackStateCardProps {
    state: FeedbackState;
    maxActions: number;
    formatTimeSince: FormatTimeSince;
}

const getCardColorClass = (totalActions: number, maxActions: number): string => {
    if (totalActions === 0) return 'bg-gray-50 border-gray-200';

    // Calculate intensity based on the ratio of current actions to max actions
    const intensity = Math.min(totalActions / Math.max(maxActions, 1), 1);

    if (intensity <= 0.2) return 'bg-blue-50 border-blue-200';
    if (intensity <= 0.4) return 'bg-blue-100 border-blue-300';
    if (intensity <= 0.6) return 'bg-blue-200 border-blue-400';
    if (intensity <= 0.8) return 'bg-blue-300 border-blue-500';
    return 'bg-blue-400 border-blue-600';
};

export const UserFeedbackStateCard = ({
    state,
    maxActions,
    formatTimeSince,
}: UserFeedbackStateCardProps) => {
    const { formatDate } = useFormatUtils();
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const actionEntries = Object.entries(state.actionCounts || {}) as [string, number][];
    const sortedActionEntries = [...actionEntries].sort((a, b) => a[0].localeCompare(b[0]));
    const totalActions = sortedActionEntries.reduce((sum, [, count]) => sum + count, 0);
    const lastSubmission = state.lastFeedbackSubmissionTime
        ? formatTimeSince(state.lastFeedbackSubmissionTime)
        : 'No submission';

    // Use the embedded feedback submissions
    const userFeedback = state.userFeedbackSubmissions || [];

    const cardColorClass = getCardColorClass(totalActions, maxActions);

    const hasFeedback = userFeedback.length > 0;
    const userEmail = hasFeedback ? userFeedback.find(f => f.userEmail)?.userEmail : null;

    const handleCardClick = () => {
        if (hasFeedback) {
            setIsPopupOpen(!isPopupOpen);
        }
    };

    const handlePopupClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleClosePopup = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsPopupOpen(false);
    };

    return (
        <article
            className={`group relative flex flex-col gap-2 rounded-md p-3 pb-10 shadow-sm transition-all duration-100 ${cardColorClass} ${hasFeedback ? 'border-2 border-orange-300 cursor-pointer lg:cursor-default' : ''}`}
            onClick={handleCardClick}
        >
            {/* User ID and Links - wraps on mobile */}
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-gray-900 truncate flex-1 min-w-0">{state.id}</p>
                <UserLinks userId={state.id} />
            </div>

            {/* Action breakdown and total - wraps on mobile */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-0">
                    <CompactActionBreakdown entries={sortedActionEntries} />
                </div>
                <span title="Total Actions" className="flex items-center justify-center gap-1 text-lg font-bold text-gray-900 bg-white/60 px-2 py-1 rounded whitespace-nowrap">
                    <SquarePlus size={20} className="text-gray-600" /> {totalActions}
                </span>
            </div>
            <div className="absolute bottom-1 right-1 flex flex-col items-end text-[10px] text-gray-900">
                <div className="flex items-center gap-0.5" title="Last feedback submission">
                    <Megaphone size={10} />
                    <span>{lastSubmission}</span>
                </div>
                <div className="flex items-center gap-0.5" title="Last updated">
                    <RefreshCw size={10} />
                    <span>{formatTimeAgo(new Date(formatDate(state.updatedAt)))}</span>
                </div>
            </div>

            {/* Feedback Popup */}
            {hasFeedback && (
                <div
                    className={`absolute left-0 right-0 bottom-full mb-2 z-50 bg-white border border-gray-300 rounded-lg shadow-xl p-4 max-w-md max-h-96 overflow-y-auto transition-all duration-200 lg:opacity-0 lg:invisible lg:group-hover:opacity-100 lg:group-hover:visible lg:pointer-events-none lg:group-hover:pointer-events-auto ${isPopupOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
                    onClick={handlePopupClick}
                >
                    {/* Close button - only visible on mobile when popup is open */}
                    {isPopupOpen && (
                        <button
                            onClick={handleClosePopup}
                            className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 transition-colors lg:hidden"
                            aria-label="Close feedback"
                        >
                            <X size={16} className="text-gray-600" />
                        </button>
                    )}

                    {/* Header with user info */}
                    <div className="mb-3 pb-3 border-b border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                            <Megaphone size={16} className="text-blue-600" />
                            <h3 className="text-sm font-semibold text-gray-900">
                                User Feedback ({userFeedback.length})
                            </h3>
                        </div>
                        {/* User Email */}
                        {userEmail && (
                            <p className="text-xs text-gray-600 mb-1 break-all">
                                📧 {userEmail}
                            </p>
                        )}
                    </div>

                    {/* Feedback submissions */}
                    <div className="space-y-3">
                        {userFeedback.map((feedback) => (
                            <div key={feedback.id} className="text-xs space-y-1 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0">
                                <p className="text-gray-800 leading-relaxed break-words">{feedback.text}</p>
                                <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500">
                                    {feedback.flow && (
                                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                            {feedback.flow}
                                        </span>
                                    )}
                                    {feedback.source && (
                                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                                            {feedback.source}
                                        </span>
                                    )}
                                    {feedback.createdAt && (
                                        <span className="text-gray-400">
                                            {formatTimeAgo(new Date(formatDate(feedback.createdAt)))}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </article>
    );
};

